import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, FileText, CreditCard, LogOut, Building2, MapPin, Menu, X, Bell, AlertCircle, Trash2, Settings, ChevronRight, ClipboardList, Handshake, ShoppingBag, Receipt, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const SidebarLayout = ({ children, user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Siempre modo oscuro
  const isDarkTheme = true;
  
  // Notificaciones in-app
  const [pendingPayments, setPendingPayments] = useState([]);
  const [showNotificationsPanel, setShowNotificationsPanel] = useState(false);

  useEffect(() => {
    fetchPendingPayments();
    
    const interval = setInterval(fetchPendingPayments, 300000);
    return () => clearInterval(interval);
  }, []);

  const fetchPendingPayments = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/pagos/pendientes-hoy`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPendingPayments(data.pagos || []);
      }
    } catch (error) {
      console.error('Error fetching pending payments:', error);
    }
  };

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setMobileMenuOpen(false);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      toast.success('Sesión cerrada correctamente');
      navigate('/login');
    } catch (error) {
      toast.error('Error al cerrar sesión');
    } finally {
      setIsLoggingOut(false);
    }
  };

  // 🔥 MENÚ DINÁMICO SEGÚN ROL
  const userRole = user?.account_role || user?.role || 'viewer';
  const isComisionista = userRole === 'comisionista';
  
  // Menú completo para usuarios normales (owner, admin, editor, viewer)
  const fullMenuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Building2, label: 'Fraccionamientos', path: '/fraccionamientos' },
    { icon: MapPin, label: 'Lotes', path: '/lotes' },
    { icon: Users, label: 'Clientes', path: '/clientes' },
    { icon: FileText, label: 'Tratos', path: '/tratos' },
    { icon: CreditCard, label: 'Pagos', path: '/pagos' },
    { icon: Handshake, label: 'Comisionistas', path: '/comisionistas' },
    { icon: DollarSign, label: 'Comisiones', path: '/comisiones' },
    { icon: ClipboardList, label: 'Historial', path: '/solicitudes' },
    { icon: Trash2, label: 'Papelera', path: '/papelera' },
    { icon: Settings, label: 'Configuración', path: '/equipo' }
  ];
  
  // Menú limitado para comisionistas
  const comisionistaMenuItems = [
    { icon: MapPin, label: 'Lotes', path: '/lotes' },
    { icon: MapPin, label: 'Mis Apartados', path: '/mis-apartados' },
    { icon: ShoppingBag, label: 'Mis Ventas', path: '/mis-ventas' },
    { icon: Receipt, label: 'Mis Comisiones', path: '/mis-comisiones' },
  ];
  
  const menuItems = isComisionista ? comisionistaMenuItems : fullMenuItems;

  const getSubscriptionBadge = () => {
    return null;
  };

  // Clases de tema - BLANCO TOTAL en modo claro
  const t = {
    // Backgrounds
    bg: isDarkTheme ? 'bg-black' : 'bg-white',
    bgMain: isDarkTheme ? 'bg-zinc-950' : 'bg-white',
    bgCard: isDarkTheme ? 'bg-zinc-900' : 'bg-white',
    bgHover: isDarkTheme ? 'hover:bg-zinc-800' : 'hover:bg-gray-100',
    
    // Borders
    border: isDarkTheme ? 'border-zinc-800' : 'border-gray-200',
    
    // Text
    text: isDarkTheme ? 'text-white' : 'text-black',
    textMuted: isDarkTheme ? 'text-zinc-400' : 'text-gray-600',
    textSubtle: isDarkTheme ? 'text-zinc-500' : 'text-gray-500',
    
    // Menu items
    menuInactive: isDarkTheme ? 'text-zinc-400 hover:bg-zinc-900 hover:text-white' : 'text-gray-600 hover:bg-gray-100 hover:text-black',
    
    // Logo - dorado en ambos modos
    logoFilter: '',  // Sin filtro - logo dorado siempre
  };

  return (
    <div className={`flex min-h-screen ${t.bgMain}`}>
      {/* Header móvil */}
      <header className={`lg:hidden fixed top-0 left-0 right-0 z-50 ${t.bg} border-b ${t.border} px-4 py-3 flex items-center justify-between`}>
        <img 
          src="https://customer-assets.emergentagent.com/job_lotesys/artifacts/fyrjj46v_Logo%20IBA%C3%91EZ%20para%20desarrollos%20inmobiliarios.png" 
          alt="Ibañez"
          className={`h-8 ${t.logoFilter}`}
        />
        <div className="flex items-center gap-2">
          {/* Notificaciones móvil */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowNotificationsPanel(!showNotificationsPanel)}
            className={`relative p-2 ${t.text}`}
          >
            <Bell className="w-5 h-5" />
            {pendingPayments.length > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {pendingPayments.length}
              </span>
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`p-2 ${t.text}`}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </header>

      {/* Panel de notificaciones */}
      {showNotificationsPanel && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-50"
            onClick={() => setShowNotificationsPanel(false)}
          />
          <div className={`fixed top-16 right-4 lg:top-4 lg:right-72 w-80 max-h-96 overflow-y-auto ${t.bgCard} rounded-xl shadow-2xl border ${t.border} z-50`}>
            <div className={`p-4 border-b ${t.border}`}>
              <h3 className={`font-bold ${t.text} flex items-center gap-2`}>
                <AlertCircle className="w-5 h-5 text-[#C6A54A]" />
                Pagos Pendientes Hoy
              </h3>
            </div>
            {pendingPayments.length === 0 ? (
              <div className="p-6 text-center">
                <p className={t.textMuted}>No hay pagos pendientes para hoy</p>
              </div>
            ) : (
              <div className={`divide-y ${t.border}`}>
                {pendingPayments.map((pago, index) => (
                  <div 
                    key={index} 
                    onClick={() => {
                      setShowNotificationsPanel(false);
                      navigate(`/pagos?trato=${pago.trato_id}&pago=${pago.pago_id}`);
                    }}
                    className={`p-4 ${t.bgHover} transition-colors cursor-pointer group`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className={`font-medium ${t.text} group-hover:text-[#C6A54A] transition-colors`}>{pago.cliente}</p>
                        <p className={`text-sm ${t.textMuted}`}>{pago.lotes}</p>
                        {pago.dias_vencido > 0 && (
                          <p className="text-xs text-red-400 mt-1">Vencido hace {pago.dias_vencido} día(s)</p>
                        )}
                      </div>
                      <div className="text-right flex items-center gap-2">
                        <div>
                          <p className="font-bold text-[#C6A54A]">
                            ${pago.monto?.toLocaleString()}
                          </p>
                          <p className={`text-xs ${t.textSubtle}`}>{pago.moneda}</p>
                        </div>
                        <ChevronRight className={`w-4 h-4 ${t.textSubtle} group-hover:text-[#C6A54A] transition-colors`} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className={`p-3 border-t ${t.border}`}>
              <Link 
                to="/pagos"
                onClick={() => setShowNotificationsPanel(false)}
                className="block text-center text-[#C6A54A] text-sm font-medium hover:underline"
              >
                Ver todos los pagos →
              </Link>
            </div>
          </div>
        </>
      )}

      {/* Overlay para cerrar menú */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/60 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed h-full ${t.bg} ${t.text} flex flex-col z-50 transition-transform duration-300 ease-in-out border-r ${t.border}
        lg:w-64 lg:translate-x-0
        w-72 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:top-0 top-0
      `}>
        {/* Logo - más compacto */}
        <div className={`p-4 border-b ${t.border} hidden lg:block`}>
          <img 
            src="https://customer-assets.emergentagent.com/job_lotesys/artifacts/fyrjj46v_Logo%20IBA%C3%91EZ%20para%20desarrollos%20inmobiliarios.png" 
            alt="Ibañez Desarrollos"
            className={`w-3/4 mx-auto ${t.logoFilter}`}
          />
        </div>

        {/* Header móvil */}
        <div className={`h-14 lg:hidden border-b ${t.border} flex items-center px-4`}>
          <span className="text-[#C6A54A] font-bold">Menú</span>
        </div>

        {/* Alerta de pagos pendientes */}
        {pendingPayments.length > 0 && (
          <div 
            onClick={() => setShowNotificationsPanel(true)}
            className={`mx-4 mt-4 p-3 rounded-lg ${isDarkTheme ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-200'} border cursor-pointer hover:opacity-80 transition-opacity`}
          >
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <div className="flex-1">
                <p className={`text-sm font-medium ${isDarkTheme ? 'text-red-400' : 'text-red-600'}`}>
                  {pendingPayments.length} pago(s) pendiente(s)
                </p>
                <p className={`text-xs ${t.textMuted}`}>
                  Click para ver detalles
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Navegación */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                  isActive
                    ? 'bg-[#C6A54A] text-black font-semibold'
                    : t.menuInactive
                }`}
              >
                <Icon className="w-5 h-5" strokeWidth={1.5} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Usuario */}
        <div className={`p-4 border-t ${t.border}`}>
          <div className="flex items-center gap-3 mb-3 px-2">
            <Avatar className="w-10 h-10 border-2 border-[#C6A54A]">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-[#C6A54A] text-black font-bold">
                {user?.name?.[0]?.toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${t.text} truncate`}>{user?.name}</p>
              <p className="text-xs text-[#C6A54A] truncate capitalize">{user?.role}</p>
            </div>
          </div>
          <Button
            data-testid="logout-button"
            onClick={handleLogout}
            disabled={isLoggingOut}
            variant="outline"
            className={`w-full ${isDarkTheme ? 'border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-white' : 'border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-black'}`}
            size="sm"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {isLoggingOut ? 'Cerrando...' : 'Cerrar Sesión'}
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className={`flex-1 lg:ml-64 pt-14 lg:pt-0 min-h-screen ${t.bgMain}`}>
        {children}
      </main>
    </div>
  );
};

export default SidebarLayout;
